import React from "react";
import Reveal from "./Reveal";

const SKILL_GROUPS = [
  {
    label: "Frontend",
    icon: "fa-solid fa-palette",
    skills: ["React", "JavaScript / TypeScript", "Tailwind CSS", "Three.js"],
  },
  {
    label: "Backend",
    icon: "fa-solid fa-server",
    skills: ["Python", "Django", "Node.js", "REST APIs", "Firebase", "Stripe"],
  },
  {
    label: "DevOps & Tooling",
    icon: "fa-solid fa-gears",
    skills: ["CI/CD", "DevOps pipelines", "Git", "VS Code extensions"],
  },
];

const EXPERIENCE = [
  {
    role: "Fullstack Engineer — CACI",
    detail: "Mid-level engineer on production systems · Secret clearance",
    badge: "Current",
  },
  {
    role: "Freelance Fullstack Developer",
    detail: "Client builds, incl. LinkJunction — a social bookmarking platform",
  },
  {
    role: "Independent Projects",
    detail: "TypeLeap, Junibo, Plastic Blame and more",
  },
];

const About = () => (
  <section id="about" className="relative min-h-screen py-28 px-6">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <p className="text-sm font-semibold tracking-[0.3em] uppercase text-ember text-shadow mb-2">
          01 · About
        </p>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white text-shadow-strong mb-12">
          The whole stack, end to end
        </h2>
      </Reveal>

      <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-10 items-start">
        {/* Left: photo + quick facts */}
        <Reveal>
          <div className="bg-dusk-900/70 border border-white/10 rounded-2xl p-8">
            <img
              src="/profile.jpg"
              alt="Alexander Lasson"
              className="rounded-2xl w-40 h-40 object-cover ring-2 ring-ember/60 mb-6"
            />
            <ul className="space-y-3 text-white/80">
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-briefcase text-ember w-5"></i>
                Fullstack Engineer at CACI
              </li>
              <li className="flex items-center gap-3">
                <i className="fab fa-github text-ember w-5"></i>
                <a
                  href="https://github.com/AlexanderLasson"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-ember transition-colors"
                >
                  github.com/AlexanderLasson
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-shield-halved text-ember w-5"></i>
                Secret clearance
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-language text-ember w-5"></i>
                Bilingual — English &amp; Swedish
              </li>
            </ul>
            <div className="flex gap-4 mt-6 text-2xl">
              <a
                href="mailto:lassonalexander@gmail.com"
                className="text-white/70 hover:text-ember transition-colors"
                aria-label="Email"
              >
                <i className="far fa-envelope"></i>
              </a>
              <a
                href="https://github.com/AlexanderLasson"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-ember transition-colors"
                aria-label="GitHub"
              >
                <i className="fab fa-github"></i>
              </a>
              <a
                href="https://www.linkedin.com/in/alexander-lasson/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-ember transition-colors"
                aria-label="LinkedIn"
              >
                <i className="fab fa-linkedin"></i>
              </a>
            </div>
          </div>
        </Reveal>

        {/* Right: narrative + experience + skills */}
        <div className="space-y-8">
          <Reveal delay={100}>
            <div className="bg-dusk-900/70 border border-white/10 rounded-2xl p-8 space-y-4 text-white/85 text-lg leading-relaxed">
              <p>
                I'm a fullstack engineer who likes owning the whole stack:
                modeling the data, designing the API, and finishing the front
                end so it feels effortless to use. I care about clean
                architecture, fast feedback loops, and software that quietly
                does its job.
              </p>
              <p>
                By day I work at CACI as a fullstack engineer building
                production software. Outside of work I ship side projects —
                from e-commerce platforms to VS Code extensions — and take on
                freelance builds for real clients. Most of it lives on my
                GitHub.
              </p>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="bg-dusk-900/70 border border-white/10 rounded-2xl p-8">
              <h3 className="font-display text-xl font-semibold text-white mb-5">
                Experience
              </h3>
              <ol className="relative border-l border-white/15 space-y-6 pl-6">
                {EXPERIENCE.map((item) => (
                  <li key={item.role} className="relative">
                    <span className="absolute -left-[1.85rem] top-1.5 w-3 h-3 rounded-full bg-ember ring-4 ring-ember/20"></span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-white">
                        {item.role}
                      </span>
                      {item.badge && (
                        <span className="text-xs font-semibold uppercase tracking-wider bg-ember/20 text-ember px-2 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-white/65 text-sm mt-1">{item.detail}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="grid sm:grid-cols-3 gap-4">
              {SKILL_GROUPS.map((group) => (
                <div
                  key={group.label}
                  className="bg-dusk-900/70 border border-white/10 rounded-2xl p-6
                             hover:border-ember/40 transition-colors duration-300"
                >
                  <i className={`${group.icon} text-ember text-xl mb-3`}></i>
                  <h4 className="font-display font-semibold text-white mb-3">
                    {group.label}
                  </h4>
                  <ul className="space-y-1.5">
                    {group.skills.map((skill) => (
                      <li key={skill} className="text-sm text-white/70">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default About;
